/**
 * Sheets 20 and 21, results and the finding drawer (mission 6 WP08 T044–T048;
 * FR-012 to FR-017, FR-023, FR-027; NFR-005, NFR-007, NFR-009; C-009, C-013;
 * ANA.2 to ANA.6, ANA.9, ANA.12, T.2, GEN.6, D.2, X.10; style guide §8
 * "Results"; research §1.18; post-plan C3, C11, C16, T24; post-tasks P1, P6,
 * P20, R7, E2; DM-01M39PR43C8WYP2S7T8VYTYTZE, DM-01M3C8MP7AV3AWFNA6R365X21T).
 *
 * The §8 layout: the run header (with NOT DELIVERABLE in the title row when
 * the run is not deliverable), the retention line, three severity chips that
 * filter, the ANA.4 legend, the T.2 band (verbatim, undismissible), the
 * truncation band, the tabs By rule · By record · Trailer reconciliation, and
 * a 260 px rail with the Output and Run details cards. Deferred callouts
 * (lineage, portfolio analytics, the PDF summary) are absent, not disabled
 * (C-009, C-013).
 *
 * The assistant (mission 8 WP09 T045, T046; FR-012, FR-013, FR-020, FR-025,
 * NFR-007; SAT.1, T.2; 20/13, 22/11, 22/13; OR-9, OR-10; squad U6, U10, S23;
 * research §1.19, §1.20): the page renders inside `AssistantDock`. Open
 * (`?assistant=open`), the rail is dropped and `OutputCard variant="compact"`
 * sits above the tabs; the T.2 band stays in both states. The dock's provider
 * is a child of this component, so its effect consumes a cross-route prefill
 * (the catalog's, the ack modal's) in the same commit as the URL
 * normalisation below it, before `setSearchParams` drops the router state
 * (WP07 review). The retention line is `retentionLineWithConversation` with a
 * `<time>` (U10, A-L3); an invalid `?rule=` says `unknownRule` (A-L1); a
 * failed first by-rule read says `severityCountsUnavailable` with Retry
 * instead of skeleton chips (A-L5).
 *
 * Reads, all waiting for `ready` (C11): `['run', id, 'rule-counts']` once, for
 * the footer's M, the drawer fallback and the truncation band (C3, C16); and
 * the current by-rule page on every tab, because the chips and the drawer need
 * it. Only `severity` goes to the by-rule route; rule, field, segment and text
 * narrow the loaded page (the spec amendment). Nothing polls here, and
 * `onRunChanged` is not called: nothing this screen does changes the run.
 */
import { useCallback, useMemo, useRef, type ReactNode, type RefObject } from 'react'
import { useQuery } from '@tanstack/react-query'

import { AssistantDock } from '@/components/trellis/assistant/AssistantDock'
import { AssistantPanel } from '@/components/trellis/assistant/AssistantPanel'
import { useTenant } from '@/components/trellis/AuthGate'
import { RunHeader } from '@/components/trellis/RunHeader'
import { SeverityCountChip } from '@/components/trellis/SeverityCountChip'
import { SeverityLegend } from '@/components/trellis/SeverityLegend'
import { T2Band } from '@/components/trellis/T2Band'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import type { ByRuleEntry, ByRuleFilters, SeverityCounts } from '@/lib/api/findings'
import type { Run } from '@/lib/api/runs'
import { useAssistantPanel } from '@/lib/assistant-panel'
import { date, isoDateTime } from '@/lib/format'
import { PROVISIONAL_COPY, copyText } from '@/lib/provisional-copy'
import { byRuleQuery, ruleCountsQuery } from '@/lib/queries'
import { SEVERITY_ORDER } from '@/lib/severity'

import { ByRecordTable } from './results/ByRecordTable'
import { ByRuleTable } from './results/ByRuleTable'
import { FindingDrawer } from './results/FindingDrawer'
import { OutputCard } from './results/OutputCard'
import { ReadError } from './results/ReadError'
import { RunDetailsCard } from './results/RunDetailsCard'
import { TrailerTable } from './results/TrailerTable'
import { TruncationBand } from './results/TruncationBand'
import { useCursorStack } from './results/useCursorStack'
import {
  filterRows,
  rowKey,
  useResultsFilters,
  type DrawerTarget,
  type PageFilterKey,
  type ResultsTab,
} from './results/useResultsFilters'
import type { RunScreenProps } from './types'

/** One page of the by-rule view (NFR-005; the wrappers send `limit=50`). */
const PAGE_SIZE = 50
const ALL = '__all__'

function distinct(values: Array<string | null>, current: string | null): string[] {
  const set = new Set(values.filter((v): v is string => v !== null && v !== ''))
  if (current !== null) set.add(current)
  return [...set]
}

function FilterSelect({
  label,
  allLabel,
  value,
  options,
  onChange,
}: {
  label: string
  allLabel: string
  value: string | null
  options: string[]
  onChange(value: string | null): void
}) {
  return (
    <Select value={value ?? ALL} onValueChange={(v) => onChange(v === ALL ? null : v)}>
      <SelectTrigger aria-label={label} className="w-[200px]">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>{allLabel}</SelectItem>
        {options.map((o) => (
          <SelectItem key={o} value={o}>
            {o}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

/** The docked panel for this run; the tenant is read only while it is open (FR-012). */
function RunAssistant({ run }: { run: Run }) {
  const tenant = useTenant()
  return <AssistantPanel runId={run.run_id} tenantName={tenant.data?.legal_name ?? ''} warnCount={run.warn_count ?? 0} />
}

/** 22/11 (U10, A-L3): the retention line naming the conversation, its date in a `<time>`. */
function RetentionLine({ expiresAt }: { expiresAt: string }) {
  const day = date(expiresAt)
  const [before, ...after] = PROVISIONAL_COPY.retentionLineWithConversation.render({ expiresAt: day }).split(day)
  return (
    <p className="text-caption text-navy-600">
      {before}
      <time dateTime={isoDateTime(expiresAt)}>{day}</time>
      {after.join(day)}
    </p>
  )
}

/** Inside the dock: open drops the rail and puts the compact row above the tabs (22/13, U6, S23). */
function Layout({ run, root, head, tabs, drawer }: { run: Run; root: RefObject<HTMLDivElement>; head: ReactNode; tabs: ReactNode; drawer: ReactNode }) {
  const { open } = useAssistantPanel()
  return (
    <div ref={root} className={open ? 'flex flex-col gap-4' : 'grid grid-cols-[minmax(0,1fr)_260px] items-start gap-6'}>
      <div className="flex min-w-0 flex-col gap-4">
        {head}
        {open ? <OutputCard run={run} variant="compact" /> : null}
        {tabs}
      </div>
      {open ? null : (
        <aside data-results-rail="" className="flex w-[260px] flex-col gap-4">
          <OutputCard run={run} />
          <RunDetailsCard run={run} />
        </aside>
      )}
      {drawer}
    </div>
  )
}

export default function Results({ run }: RunScreenProps) {
  const ready = run.state === 'ready'
  const f = useResultsFilters()
  const paging = useCursorStack(`${f.tab}|${f.tableSeverity ?? ''}`)

  const filters: ByRuleFilters = {}
  if (f.tableSeverity !== null) filters.severity = f.tableSeverity
  if (paging.cursor !== undefined) filters.cursor = paging.cursor
  const byRule = useQuery(byRuleQuery(run.run_id, filters, ready))
  const ruleCounts = useQuery(ruleCountsQuery(run.run_id, ready))
  // mission 7 WP08 T045 (F2; FR-027; squad U13): the rules with findings, for the header's meta line.
  const firedRules = ruleCounts.data ? Object.values(ruleCounts.data.rule_counts).filter((n) => n > 0).length : undefined

  // The counts are run-wide on every page, so the chips keep them while the next page loads.
  const lastCounts = useRef<SeverityCounts | undefined>(undefined)
  if (byRule.data) lastCounts.current = byRule.data.counts
  const counts = byRule.data?.counts ?? lastCounts.current

  const entries = useMemo<ByRuleEntry[]>(() => byRule.data?.entries ?? [], [byRule.data])
  const rows = useMemo(
    () => filterRows(entries, { ruleId: f.ruleId, field: f.field, segment: f.segment, q: f.q }),
    [entries, f.ruleId, f.field, f.segment, f.q],
  )
  const filtered = f.ruleId !== null || f.field !== null || f.segment !== null || f.q.trim() !== ''
  const next = byRule.data?.next_cursor
  const truncated = ruleCounts.data?.truncated_rules ?? byRule.data?.truncated_rules ?? []
  // owner decision 2026-09-26 (decision 4): the footer's row total, known only on the last page, unfiltered.
  const rowTotal = byRule.data && next === undefined && !filtered ? paging.index * PAGE_SIZE + entries.length : null

  const root = useRef<HTMLDivElement>(null)
  const rowEls = useRef(new Map<string, HTMLTableRowElement>())
  const rowRef = useCallback(
    (key: string) => (el: HTMLTableRowElement | null) => {
      if (el) rowEls.current.set(key, el)
      else rowEls.current.delete(key)
    },
    [],
  )

  const originFor = useCallback((d: DrawerTarget): HTMLElement | null => {
    const row =
      d.severity !== null
        ? rowEls.current.get(rowKey(d.rule, d.severity))
        : [...rowEls.current.entries()].find(([key]) => key.startsWith(`${d.rule}|`))?.[1]
    if (row?.isConnected) return row
    // The row is not on the page: the page title takes focus (§7.11, §9).
    // mission 6 WP13: RunHeader's h1 carries tabIndex={-1}; no DOM write here.
    return root.current?.querySelector<HTMLElement>('h1') ?? null
  }, [])

  const { drawer, moveDrawer } = f
  const onMove = useCallback(
    (step: 1 | -1) => {
      if (drawer === null) return
      const at = rows.findIndex((r) => r.rule_id === drawer.rule && (drawer.severity === null || r.severity === drawer.severity))
      const to = rows[at + step]
      if (at < 0 || to === undefined) return
      moveDrawer(to.rule_id, to.severity)
    },
    [drawer, moveDrawer, rows],
  )

  const setFilter = (key: PageFilterKey) => (value: string | null) => f.setPageFilter(key, value)

  const head = (
    <>
      <RunHeader
        run={run}
        firedRules={firedRules}
        // mission 7 WP14: J3, the badge text is a register entry (P22).
        actions={run.deliverable ? undefined : <Badge status="not-deliverable">{copyText('notDeliverableBadge')}</Badge>}
      />
      {run.expires_at ? <RetentionLine expiresAt={run.expires_at} /> : null}
      <div className="flex flex-wrap items-center gap-2">
        {counts === undefined && byRule.isError ? (
          // FR-020 A-L5: a failed first read says so, with Retry, instead of skeletons for ever.
          <p data-counts-unavailable="" className="flex items-center gap-3 text-caption text-navy-600">
            {copyText('severityCountsUnavailable')}
            <Button variant="secondary" size="sm" loading={byRule.isFetching} onClick={() => void byRule.refetch()}>
              {copyText('retry')}
            </Button>
          </p>
        ) : counts ? (
          SEVERITY_ORDER.map((sev) =>
            f.tab === 'by-rule' ? (
              <SeverityCountChip
                key={sev}
                severity={sev}
                count={counts[sev]}
                selected={f.tableSeverity === sev}
                onSelect={() => f.setSeverity(f.tableSeverity === sev ? null : sev)}
              />
            ) : (
              <SeverityCountChip
                key={sev}
                severity={sev}
                count={counts[sev]}
                selected={f.tableSeverity === sev}
                onSelect={() => undefined}
                disabled
                disabledReason={copyText('chipsDisabledReason')}
              />
            ),
          )
        ) : (
          SEVERITY_ORDER.map((sev) => <Skeleton key={sev} className="h-7 w-20 rounded-full" />)
        )}
      </div>
      <SeverityLegend />
      <T2Band />
      <TruncationBand truncated={truncated} runId={run.run_id} />
      {f.ruleInvalid ? (
        <p data-drawer-not-found="" className="text-body-sm text-navy-700">
          {/* FR-020 A-L1: an invalid ?rule= is an unknown rule, not a missing page. */}
          {copyText('unknownRule')}
        </p>
      ) : null}
    </>
  )
  const tabs = (
    <Tabs value={f.tab} onValueChange={(v) => f.setTab(v as ResultsTab)}>
      <TabsList>
        <TabsTrigger value="by-rule">By rule</TabsTrigger>
        <TabsTrigger value="by-record">By record</TabsTrigger>
        <TabsTrigger value="trailer">Trailer reconciliation</TabsTrigger>
      </TabsList>
      <TabsContent value="by-rule" className="flex flex-col gap-3">
        <div data-results-toolbar="" className="flex flex-wrap items-center gap-3">
          <FilterSelect
            label="Rule"
            allLabel="All rules"
            value={f.ruleId}
            options={distinct(entries.map((e) => e.rule_id), f.ruleId)}
            onChange={setFilter('rule_id')}
          />
          <FilterSelect
            label="Field"
            allLabel="All fields"
            value={f.field}
            options={distinct(entries.map((e) => e.field), f.field)}
            onChange={setFilter('field')}
          />
          <FilterSelect
            label="Segment"
            allLabel={copyText('allSegments')}
            value={f.segment}
            options={distinct(entries.map((e) => e.segment), f.segment)}
            onChange={setFilter('segment')}
          />
          <Input
            type="search"
            aria-label="Search rules"
            className="w-[240px]"
            value={f.q}
            onChange={(e) => f.setPageFilter('q', e.target.value)}
          />
        </div>
        {byRule.isError ? (
          <ReadError error={byRule.error} onStartAgain={paging.reset} />
        ) : (
          <ByRuleTable
            rows={rows}
            loading={byRule.isPending}
            footer={{
              start: paging.index * PAGE_SIZE + 1,
              total: rowTotal,
              filtered,
              morePages: paging.index > 0 || next !== undefined,
            }}
            onOpen={f.openDrawer}
            selected={f.drawer}
            rowRef={rowRef}
            paging={{
              canPrevious: paging.index > 0,
              canNext: next !== undefined,
              onPrevious: paging.previous,
              onNext: () => next !== undefined && paging.next(next),
            }}
          />
        )}
      </TabsContent>
      <TabsContent value="by-record">
        <ByRecordTable runId={run.run_id} ready={ready} />
      </TabsContent>
      <TabsContent value="trailer">
        <TrailerTable runId={run.run_id} ready={ready} />
      </TabsContent>
    </Tabs>
  )
  const drawerEl = (
    <FindingDrawer
      run={run}
      rows={rows}
      ruleCounts={ruleCounts.data}
      truncated={byRule.data?.truncated_rules ?? []}
      loading={byRule.isPending || ruleCounts.isPending}
      drawer={f.drawer}
      onMove={onMove}
      onClose={f.closeDrawer}
      originFor={originFor}
      drawerCloseEdit={f.drawerCloseEdit}
    />
  )

  return (
    <AssistantDock runId={run.run_id} panel={<RunAssistant run={run} />}>
      <Layout run={run} root={root} head={head} tabs={tabs} drawer={drawerEl} />
    </AssistantDock>
  )
}
